import React, { Children, cloneElement, Component, isValidElement } from 'react';
import ReactDOM from 'react-dom';

import { Alignment, Placement } from '@philips-emr/tws-constants';
import { DocumentContext } from '@philips-emr/tws-core/lib/component/context';
import { arrayOf, bool, element, expression, number, object, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, enumValues, getBestPosition, isDefined, isEnumValid } from '@philips-emr/tws-utils';

import './tooltip.scss';
import { TooltipTrigger } from './constants';

class Tooltip extends Component {

  static contextType = DocumentContext;

  static propTypes = {
    alignment: string,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    placement: string,
    showArrow: bool,
    trigger: oneOf(enumValues(TooltipTrigger)),
    value: oneOfType([expression, number]),
    boundaryElement: object,
    offsetTop: number,
    offsetLeft: number,
    zIndex: number,
    multiline: bool,
  };

  static defaultProps = {
    alignment: Alignment.CENTER,
    placement: Placement.TOP,
    showArrow: true,
    trigger: TooltipTrigger.HOVER,
    offsetTop: 0,
    offsetLeft: 0,
    multiline: false,
  };

  state = {};

  onClick = () => {
    this.setState(({ open }) => ({ open: !open }));
  };

  onMouseEnter = () => {
    const { trigger } = this.props;

    if (trigger === TooltipTrigger.HOVER) {
      return this.setState({ open: true }, this.bindScrollEvent);
    }

    const { hostElement = {} } = this.state;
    const { offsetWidth, scrollWidth } = hostElement;

    this.setState({ open: scrollWidth > offsetWidth }, this.bindScrollEvent);
  };

  onMouseLeave = () => {
    this.setState({ open: false });
  };

  hostRef = (hostElement) => {
    this.setState({ hostElement });
  };

  guestRef = (guestElement) => {
    this.setState({ guestElement });
  };

  closeTooltip = () => {
    this.setState({ open: false }, this.unbindScrollEvent);
  };

  bindScrollEvent = () => {
    const { document } = this.context;

    document.addEventListener('scroll', this.closeTooltip, true);
  };

  unbindScrollEvent = () => {
    const { document } = this.context;

    document.removeEventListener('scroll', this.closeTooltip, true);
  };

  getClassName(placement) {
    const { multiline } = this.props;
    return classNames({
      'c-tooltip': true,
      [`c-tooltip--${placement}`]: isEnumValid(Placement, placement),
      'c-tooltip--multiline': multiline,
    });
  }

  renderTarget() {
    const { children } = this.props;
    const [target] = Children.toArray(children);

    if (!isValidElement(target)) {
      return null;
    }

    return cloneElement(target, {
      ref: this.hostRef,
      onMouseEnter: this.onMouseEnter,
      onMouseLeave: this.onMouseLeave,
    });
  }

  renderTooltip() {
    const { value, placement, alignment, showArrow, boundaryElement, offsetTop, offsetLeft, zIndex } = this.props;
    const { hostElement, guestElement } = this.state;
    const { document, window } = this.context;

    if (!isDefined(value) || value === null) {
      return null;
    }

    const best = getBestPosition({
      alignment,
      placement,
      hostElement,
      guestElement,
      offset: { top: offsetTop, left: offsetLeft },
      arrowSize: showArrow ? 8 : 0,
      boundaryElement,
      window,
    });

    const tooltip = (
      <div className={this.getClassName(best.placement)} style={{ ...best.guest, zIndex }} ref={this.guestRef}>
        {showArrow && <div className="c-tooltip__arrow" style={{ ...best.arrow }}></div>}
        <div className="c-tooltip__content"> {msg(value)} </div>
      </div>
    );

    return ReactDOM.createPortal(tooltip, document.body);
  }

  render() {
    const { open } = this.state;

    return (
      <>
        {open && this.renderTooltip()}
        {this.renderTarget()}
      </>
    );
  }
}

export default Tooltip;
